import { useRef, useState } from 'react';
import { useVault } from '../api/hooks';
import { Stack, Text, Input, Panel, List, ListItem, Tag, Row, EmptyState, Button, ArtifactView } from '../ui';
import { api } from '../api/client';

export function Vault() {
  const { data: items, loading, refresh } = useVault();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<any>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadType, setUploadType] = useState<'kyc'|'upload'|'dd'>('kyc');
  const fileRef = useRef<HTMLInputElement>(null);

  const filtered = items.filter((x: any) => q === '' || x.title?.toLowerCase().includes(q.toLowerCase()));

  async function uploadFile(file: File) {
    setUploadBusy(true);
    try {
      const r = await api.uploadDoc(file, { type: uploadType });
      refresh();
      // Auto-open the newly created artifact
      const fresh = await api.artifactContent(r.artifactId).catch(() => null);
      if (fresh?.artifact) setSelected(fresh.artifact);
    } catch (e: any) {
      alert('upload failed: ' + e.message);
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
      <section className="md:col-span-8">
        <Stack gap={3}>
          <Stack gap={1}>
            <Text variant="display">Vault</Text>
            <Text muted variant="small">Every artifact, with provenance.</Text>
          </Stack>

          <Panel title="Upload document" action={
            <select value={uploadType} onChange={e => setUploadType(e.target.value as any)} className="h-9 sm:h-8 text-sm sm:text-xs hairline rounded-sm px-2 bg-bg">
              <option value="kyc">KYC package</option>
              <option value="dd">Due diligence</option>
              <option value="upload">General upload</option>
            </select>
          }>
            <Stack gap={2}>
              <Text variant="small" muted>
                The agent extracts entities, checks sanctions, and flags risks automatically.
              </Text>
              <div
                onClick={() => !uploadBusy && fileRef.current?.click()}
                className={`hairline rounded-sm p-4 text-center cursor-pointer transition-colors ${
                  uploadBusy ? 'opacity-50 cursor-not-allowed' : 'hover:bg-whisper'
                }`}
              >
                <input
                  ref={fileRef}
                  type="file"
                  onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])}
                  className="hidden"
                  disabled={uploadBusy}
                />
                <Text variant="small" muted>
                  {uploadBusy ? 'uploading…' : 'tap to choose file'}
                </Text>
              </div>
            </Stack>
          </Panel>

          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="search documents, kyc reports, memos…" />
          <Panel>
            {loading ? <Text variant="label">loading…</Text>
              : filtered.length === 0
                ? <EmptyState title={items.length === 0 ? 'no artifacts yet' : 'no matches'} hint={items.length === 0 ? 'agents produce artifacts as deals progress; you can also upload above' : undefined} />
                : <List>
                    {filtered.map((it: any) => (
                      <ListItem key={it.id}>
                        <button className="w-full text-left hover:bg-whisper -mx-2 px-2 py-0.5 rounded-sm transition-colors" onClick={() => setSelected(selected?.id === it.id ? null : it)}>
                          <Row justify="between">
                            <Stack gap={0.5}>
                              <Text variant="small" className="font-medium">{it.title}</Text>
                              <Row gap={1}>
                                <Tag>{it.type}</Tag>
                                {it.reviewStatus && it.reviewStatus !== 'pending' && (
                                  <Tag>{it.reviewStatus}</Tag>
                                )}
                                {it.producedByAgent && <Text variant="label">by {it.producedByAgent} agent</Text>}
                              </Row>
                            </Stack>
                            <Text variant="label" className="tnum">v{it.version}</Text>
                          </Row>
                        </button>
                      </ListItem>
                    ))}
                  </List>}
          </Panel>
        </Stack>
      </section>

      <aside className="md:col-span-4">
        {selected ? (
          <Panel>
            <ArtifactView artifact={selected} onClose={() => setSelected(null)} />
          </Panel>
        ) : (
          <Panel>
            <Text variant="label" muted>click any document to view details and extracted entities</Text>
          </Panel>
        )}
      </aside>
    </div>
  );
}
